import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import TimeLogModal from '@/components/TimeLogModal';
import QCSheetSelectorModal from '@/components/QCSheetSelectorModal';
import ChecklistSelectorModal from '@/components/ChecklistSelectorModal';
import { 
  ArrowLeft, Save, Upload, Download, Trash2, FileText, Image, 
  File, FolderOpen, Plus, X, Eye, MessageSquare, Send, AlertCircle,
  CheckCircle, Clock, User, ChevronDown, ChevronRight, Edit, 
  Calendar, FileCheck, ClipboardList, Mail, Wrench, GitBranch, Settings
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILE_CATEGORIES = [
  'Drawings',
  'Pictures',
  'Material Certs',
  'QC Sheets',
  'Receipts',
  'Weld Maps',
  'Inspection Notes',
  'Invoices'
];

const ProjectDetailEnhanced = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Drawings');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [fileNotes, setFileNotes] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messageType, setMessageType] = useState('comment');
  const [messagePriority, setMessagePriority] = useState('normal');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showTimeLogModal, setShowTimeLogModal] = useState(false);
  const [showQCModal, setShowQCModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  
  // Sub-task management states
  const [expandedMilestones, setExpandedMilestones] = useState({});
  const [showAddSubTaskModal, setShowAddSubTaskModal] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [editingSubTask, setEditingSubTask] = useState(null);
  const [subTaskForm, setSubTaskForm] = useState({
    name: '',
    description: '',
    estimated_hours: 0,
    status: 'todo',
    assigned_to: '',
    due_date: ''
  });

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id) {
      loadProject();
      loadMessages();
    }
  }, [id]);

  const loadProject = async () => {
    setLoading(true);
    try {
      const [projectRes, timeLogsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/time-logs?project_id=${id}`).catch(() => ({ data: [] }))
      ]);
      
      const projectData = projectRes.data;
      
      // Try to load milestones separately if they exist
      try {
        const milestonesRes = await axios.get(`${API}/projects/${id}/milestones`);
        projectData.milestones = milestonesRes.data || [];
      } catch (error) {
        // If milestones endpoint doesn't exist, use milestones from project object
        console.log('Using milestones from project object');
        projectData.milestones = projectData.milestones || [];
      }
      
      // Calculate total hours from time logs
      const timeLogs = timeLogsRes.data || [];
      const hoursFromLogs = timeLogs.reduce((sum, log) => sum + (log.hours || 0), 0);
      
      // Add calculated fields for display (matching workboard logic)
      projectData.hours_from_logs = hoursFromLogs;
      projectData.total_labour_hours = (projectData.actual_labour_hours || 0) + hoursFromLogs;
      
      setProject(projectData);
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}/interaction-summary`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const toggleMilestone = (milestoneId) => {
    setExpandedMilestones(prev => ({
      ...prev,
      [milestoneId]: !prev[milestoneId]
    }));
  };

  const handleAddSubTask = (milestone) => {
    setSelectedMilestone(milestone);
    setEditingSubTask(null);
    setSubTaskForm({
      name: '',
      description: '',
      estimated_hours: 0,
      status: 'todo',
      assigned_to: '',
      due_date: ''
    });
    setShowAddSubTaskModal(true);
  };

  const handleEditSubTask = (milestone, subTask) => {
    setSelectedMilestone(milestone);
    setEditingSubTask(subTask);
    setSubTaskForm({
      name: subTask.name,
      description: subTask.description || '',
      estimated_hours: subTask.estimated_hours,
      status: subTask.status,
      assigned_to: subTask.assigned_to || '',
      due_date: subTask.due_date ? subTask.due_date.split('T')[0] : ''
    });
    setShowAddSubTaskModal(true);
  };

  const handleSaveSubTask = async () => {
    if (!subTaskForm.name.trim()) {
      toast.error('Sub-task name is required');
      return;
    }

    // Validate total hours don't exceed milestone hours
    const currentSubTasks = selectedMilestone.sub_tasks || [];
    let totalEstimatedHours = parseFloat(subTaskForm.estimated_hours) || 0;
    
    // If editing, subtract the old hours before adding new hours
    if (editingSubTask) {
      const oldHours = editingSubTask.estimated_hours || 0;
      totalEstimatedHours = totalEstimatedHours - oldHours;
    }
    
    // Add up all other sub-task hours
    currentSubTasks.forEach(st => {
      if (!editingSubTask || st.id !== editingSubTask.id) {
        totalEstimatedHours += (st.estimated_hours || 0);
      }
    });

    const milestoneHours = selectedMilestone.hours_estimated || 0;
    
    // If exceeds, show warning and require confirmation
    if (totalEstimatedHours > milestoneHours) {
      const exceed = (totalEstimatedHours - milestoneHours).toFixed(1);
      const message = `⚠️ WARNING: Sub-task hours (${totalEstimatedHours.toFixed(1)}h) exceed milestone total (${milestoneHours}h) by ${exceed}h.\n\nThis requires supervisor assessment and approval.\n\nDo you want to proceed anyway?`;
      
      if (!confirm(message)) {
        return;
      }
      
      // Add a note to the sub-task about supervisor review needed
      subTaskForm.description = (subTaskForm.description || '') + '\n\n⚠️ SUPERVISOR REVIEW REQUIRED: Hours exceed milestone allocation.';
    }

    try {
      if (editingSubTask) {
        // Update existing sub-task
        await axios.put(
          `${API}/projects/${id}/milestones/${selectedMilestone.id}/subtasks/${editingSubTask.id}`,
          subTaskForm
        );
        toast.success('Sub-task updated successfully');
      } else {
        // Create new sub-task
        await axios.post(
          `${API}/projects/${id}/milestones/${selectedMilestone.id}/subtasks`,
          subTaskForm
        );
        toast.success('Sub-task added successfully');
      }
      
      setShowAddSubTaskModal(false);
      loadProject();
    } catch (error) {
      toast.error(`Failed to ${editingSubTask ? 'update' : 'add'} sub-task`);
    }
  };

  const handleDeleteSubTask = async (milestoneId, subTaskId) => {
    if (!confirm('Are you sure you want to delete this sub-task?')) return;

    try {
      await axios.delete(`${API}/projects/${id}/milestones/${milestoneId}/subtasks/${subTaskId}`);
      toast.success('Sub-task deleted successfully');
      loadProject();
    } catch (error) {
      toast.error('Failed to delete sub-task');
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await axios.put(`${API}/projects/${id}`, project);
      toast.success('Project updated successfully');
      // Reload project to get fresh data
      await loadProject();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update project');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubTasksProgress = (milestone) => {
    const subTasks = milestone.sub_tasks || [];
    if (subTasks.length === 0) return null;
    
    const completed = subTasks.filter(st => st.status === 'completed').length;
    return { completed, total: subTasks.length, percentage: Math.round((completed / subTasks.length) * 100) };
  };

  const getTotalFilesCount = () => {
    if (!project?.job_folder) return 0;
    return Object.values(project.job_folder).reduce((sum, files) => sum + files.length, 0);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFile(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', selectedCategory);
        if (fileNotes) {
          formData.append('notes', fileNotes);
        }

        await axios.post(`${API}/projects/${id}/files`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success(`${files.length} file(s) uploaded successfully`);
      setFileNotes('');
      loadProject();
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/projects/${id}/files/${fileId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(`${API}/projects/${id}/files/${fileId}`);
      toast.success('File deleted successfully');
      loadProject();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <Image size={18} className="text-blue-500" />;
    if (['pdf'].includes(ext)) return <FileText size={18} className="text-red-500" />;
    return <File size={18} className="text-muted-foreground" />;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-8">
        <Card className="border-border">
          <CardContent className="py-12">
            <p className="text-center text-muted-foreground">Project not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="project-detail-page">
      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <Button
          onClick={() => navigate('/projects')}
          variant="outline"
          size="sm"
          className="font-mono uppercase border-gray-400 hover:bg-gray-100 text-gray-900 font-bold"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2">
            {project.project_name}
          </h1>
          <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
            Project Management & Job Folder
          </p>
        </div>
        <Button
          onClick={() => setShowTimeLogModal(true)}
          className="font-mono uppercase"
        >
          <Clock size={16} className="mr-2" />
          Log Time
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/workboard/projects/${id}`)}
          className="font-mono text-xs border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
        >
          <GitBranch size={14} className="mr-1" />
          Workboard
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowQCModal(true)}
          className="font-mono text-xs border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
        >
          <FileCheck size={14} className="mr-1" />
          QC Sheets
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowChecklistModal(true)}
          className="font-mono text-xs border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
        >
          <ClipboardList size={14} className="mr-1" />
          Checklists
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/tools/rfq?project_id=${id}`)}
          className="font-mono text-xs border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
        >
          <Mail size={14} className="mr-1" />
          RFQ
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/tools/inventory?project_id=${id}`)}
          className="font-mono text-xs border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
        >
          <Wrench size={14} className="mr-1" />
          Inventory
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/tools/decision-tree?project_id=${id}`)}
          className="font-mono text-xs border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
        >
          <Settings size={14} className="mr-1" />
          Decisions
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowTimeLogModal(true)}
          className="font-mono text-xs border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
        >
          <Clock size={14} className="mr-1" />
          Log Time
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setActiveTab('messages')}
          className="font-mono text-xs border-gray-300 hover:bg-gray-100 hover:border-gray-400 text-gray-900 font-semibold"
        >
          <MessageSquare size={14} className="mr-1" />
          Messages
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b-2 border-gray-300">
        <div className="flex gap-4">
          {['overview', 'milestones', 'files', 'messages'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 px-2 font-mono uppercase text-sm tracking-wide border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600 font-bold'
                  : 'border-transparent text-gray-700 hover:text-gray-900 hover:border-gray-400 font-semibold'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Project Info (Always Visible) */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="uppercase text-xs tracking-wide">Client Name</Label>
                <p className="mt-1 font-semibold">{project.client_name}</p>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-wide">Client Email</Label>
                <p className="mt-1 font-mono text-sm">{project.client_email}</p>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-wide text-gray-900 font-bold">Quote Total</Label>
                <p className="mt-1 font-mono text-2xl font-bold text-primary">
                  ${project.quote_total?.toFixed(2) || '0.00'}
                </p>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-wide text-gray-900 font-bold">Total Hours Tracked</Label>
                <div className="mt-1 p-3 bg-blue-50 border-2 border-blue-300 rounded">
                  <p className="font-mono text-2xl font-bold text-blue-900">
                    {project.total_labour_hours?.toFixed(1) || project.actual_labour_hours?.toFixed(1) || '0.0'}h
                  </p>
                  <p className="text-xs text-blue-700 font-semibold mt-1">
                    Base: {project.actual_labour_hours || 0}h + Time Logs: {project.hours_from_logs?.toFixed(1) || 0}h
                  </p>
                  <p className="text-xs text-gray-600 font-medium mt-1">
                    (Matches Workboard: {project.total_labour_hours?.toFixed(1) || '0.0'}h)
                  </p>
                </div>
              </div>
              <div>
                <Label htmlFor="status" className="uppercase text-xs tracking-wide">Status</Label>
                <select
                  id="status"
                  className="mt-1 w-full px-3 py-2 bg-background border border-input rounded-sm font-mono text-sm"
                  value={project.status}
                  onChange={(e) => setProject({ ...project, status: e.target.value })}
                >
                  <option value="Quoted">Quoted</option>
                  <option value="Won">Won</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                  <option value="Invoiced">Invoiced</option>
                </select>
              </div>
              <div>
                <Label htmlFor="labour-hours" className="uppercase text-xs tracking-wide text-gray-900 font-bold">
                  Actual Labour Hours
                </Label>
                <Input
                  id="labour-hours"
                  type="number"
                  step="0.1"
                  className="mt-1 font-mono border-gray-300 text-gray-900 font-medium"
                  value={project.actual_labour_hours || 0}
                  onChange={(e) =>
                    setProject({ ...project, actual_labour_hours: parseFloat(e.target.value) || 0 })
                  }
                />
                {project.hours_from_logs > 0 && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-900 font-semibold">
                      📊 Total: {project.total_labour_hours?.toFixed(1)}h 
                      <span className="text-blue-700 ml-1">
                        (Base: {project.actual_labour_hours || 0}h + Time Logs: {project.hours_from_logs?.toFixed(1)}h)
                      </span>
                    </p>
                  </div>
                )}
              </div>
              <div>
                <Label htmlFor="material-cost" className="uppercase text-xs tracking-wide text-gray-900 font-bold">
                  Actual Material Cost
                </Label>
                <Input
                  id="material-cost"
                  type="number"
                  step="0.01"
                  className="mt-1 font-mono border-gray-300 text-gray-900 font-medium"
                  value={project.actual_material_cost || 0}
                  onChange={(e) =>
                    setProject({ ...project, actual_material_cost: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
              <Button
                onClick={handleUpdate}
                disabled={updating}
                className="w-full font-mono uppercase tracking-wider"
              >
                <Save size={16} className="mr-2" />
                {updating ? 'Updating...' : 'Update Project'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Tab Content */}
        <div className="lg:col-span-2">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                  Project Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="uppercase text-xs tracking-wide">Description</Label>
                  <Textarea
                    className="mt-2 font-mono text-sm"
                    rows={4}
                    value={project.description || ''}
                    onChange={(e) => setProject({ ...project, description: e.target.value })}
                    placeholder="Project description..."
                  />
                </div>
                <div>
                  <Label className="uppercase text-xs tracking-wide">Notes</Label>
                  <Textarea
                    className="mt-2 font-mono text-sm"
                    rows={4}
                    value={project.notes || ''}
                    onChange={(e) => setProject({ ...project, notes: e.target.value })}
                    placeholder="Internal notes..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="uppercase text-xs tracking-wide">Start Date</Label>
                    <p className="mt-1 text-sm">{project.start_date ? new Date(project.start_date).toLocaleDateString() : 'Not set'}</p>
                  </div>
                  <div>
                    <Label className="uppercase text-xs tracking-wide">Completion Date</Label>
                    <p className="mt-1 text-sm">{project.completion_date ? new Date(project.completion_date).toLocaleDateString() : 'Not set'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Milestones Tab */}
          {activeTab === 'milestones' && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                  Project Milestones & Sub-Tasks
                </CardTitle>
              </CardHeader>
              <CardContent>
                {project.milestones && project.milestones.length > 0 ? (
                  <div className="space-y-4">
                    {project.milestones.map((milestone) => {
                      const progress = getSubTasksProgress(milestone);
                      const isExpanded = expandedMilestones[milestone.id];
                      const subTasks = milestone.sub_tasks || [];

                      return (
                        <div key={milestone.id} className="border border-border rounded-lg overflow-hidden">
                          {/* Milestone Header */}
                          <div className="p-4 bg-accent/20">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleMilestone(milestone.id)}
                                    className="h-6 w-6 p-0"
                                  >
                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                  </Button>
                                  <h3 className="text-base font-semibold">{milestone.description}</h3>
                                  {progress && (
                                    <Badge variant="secondary" className="text-xs">
                                      {progress.completed}/{progress.total} tasks
                                    </Badge>
                                  )}
                                </div>
                                <div className="ml-8 space-y-2">
                                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                    <span className="font-mono">
                                      {(milestone.hours_logged || 0).toFixed(1)}h / {milestone.hours_estimated}h
                                    </span>
                                    <span className="font-mono font-bold">
                                      {Math.min(Math.round(((milestone.hours_logged || 0) / milestone.hours_estimated) * 100), 100)}%
                                    </span>
                                  </div>
                                  {(() => {
                                    const totalSubTaskHours = subTasks.reduce((sum, st) => sum + (st.estimated_hours || 0), 0);
                                    const exceeds = totalSubTaskHours > milestone.hours_estimated;
                                    return exceeds && (
                                      <div className="flex items-center gap-2 text-xs text-red-600 font-bold">
                                        <AlertCircle size={14} />
                                        Sub-tasks exceed milestone by {(totalSubTaskHours - milestone.hours_estimated).toFixed(1)}h - Supervisor review required
                                      </div>
                                    );
                                  })()}
                                  <div className="w-full bg-border rounded-full h-2">
                                    <div 
                                      className="bg-primary rounded-full h-2 transition-all"
                                      style={{ 
                                        width: `${Math.min(((milestone.hours_logged || 0) / milestone.hours_estimated) * 100, 100)}%` 
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => handleAddSubTask(milestone)}
                                className="font-mono text-xs"
                              >
                                <Plus size={14} className="mr-1" />
                                Add Sub-Task
                              </Button>
                            </div>
                          </div>

                          {/* Sub-Tasks List */}
                          {isExpanded && subTasks.length > 0 && (
                            <div className="divide-y divide-border">
                              {subTasks.map((subTask) => (
                                <div key={subTask.id} className="p-4 hover:bg-accent/10 transition-colors">
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-3 mb-2">
                                        <Badge className={getStatusColor(subTask.status)}>
                                          {subTask.status.replace('_', ' ').toUpperCase()}
                                        </Badge>
                                        <h4 className="font-semibold text-sm">{subTask.name}</h4>
                                      </div>
                                      {subTask.description && (
                                        <p className="text-sm text-muted-foreground mb-2 ml-0">
                                          {subTask.description}
                                        </p>
                                      )}
                                      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <Clock size={12} />
                                          {subTask.actual_hours || 0}h / {subTask.estimated_hours}h
                                        </span>
                                        {subTask.assigned_to && (
                                          <span className="flex items-center gap-1">
                                            <User size={12} />
                                            {subTask.assigned_to}
                                          </span>
                                        )}
                                        {subTask.due_date && (
                                          <span className="flex items-center gap-1">
                                            <Calendar size={12} />
                                            {new Date(subTask.due_date).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleEditSubTask(milestone, subTask)}
                                      >
                                        <Edit size={14} />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteSubTask(milestone.id, subTask.id)}
                                        className="text-red-500 hover:text-red-700"
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {isExpanded && subTasks.length === 0 && (
                            <div className="p-8 text-center text-muted-foreground">
                              <p className="text-sm">No sub-tasks yet. Click "Add Sub-Task" to get started.</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No milestones defined for this project.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Files Tab */}
          {activeTab === 'files' && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
                  <FolderOpen className="inline mr-2" size={20} />
                  Digital Job Folder
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Category Tabs */}
                <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-border">
                  {FILE_CATEGORIES.map((category) => {
                    const fileCount = project.job_folder?.[category]?.length || 0;
                    return (
                      <Button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        variant={selectedCategory === category ? 'default' : 'outline'}
                        size="sm"
                        className="font-mono uppercase text-xs"
                      >
                        {category}
                        {fileCount > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 bg-primary/20 rounded-full text-xs">
                            {fileCount}
                          </span>
                        )}
                      </Button>
                    );
                  })}
                </div>

                {/* File Upload Section */}
                <div className="mb-6 p-4 border-2 border-dashed border-border rounded-sm">
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      id="file-upload"
                      className="hidden"
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                      multiple
                    />
                    <Button
                      onClick={() => document.getElementById('file-upload').click()}
                      disabled={uploadingFile}
                      size="sm"
                      className="font-mono uppercase"
                    >
                      <Upload size={14} className="mr-2" />
                      {uploadingFile ? 'Uploading...' : `Upload to ${selectedCategory}`}
                    </Button>
                    <Input
                      placeholder="Optional notes..."
                      value={fileNotes}
                      onChange={(e) => setFileNotes(e.target.value)}
                      className="flex-1 font-mono text-sm"
                      disabled={uploadingFile}
                    />
                  </div>
                </div>

                {/* Files List */}
                <div className="space-y-2">
                  {project.job_folder?.[selectedCategory]?.length > 0 ? (
                    project.job_folder[selectedCategory].map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-4 bg-card border border-border rounded-sm hover:border-primary/30 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {getFileIcon(file.original_filename)}
                          <div className="flex-1">
                            <p className="text-sm font-medium">{file.original_filename}</p>
                            <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono mt-1">
                              <span>{formatFileSize(file.file_size)}</span>
                              <span>{new Date(file.upload_date).toLocaleDateString()}</span>
                              {file.notes && <span className="text-yellow-500">Has notes</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleDownloadFile(file.id, file.original_filename)}
                            size="sm"
                            variant="ghost"
                          >
                            <Download size={14} />
                          </Button>
                          <Button
                            onClick={() => handleDeleteFile(file.id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-500"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted-foreground py-8">
                      No files in this category. Upload some files to get started.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-xl font-semibold tracking-wide uppercase text-gray-900">
                  Project Messages & Communication
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Message Form */}
                <div className="mb-6 p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
                  <h3 className="font-bold text-gray-900 mb-3">Add New Message</h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-900 font-bold text-sm">Message Type</Label>
                        <select
                          value={messageType}
                          onChange={(e) => setMessageType(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border-2 border-gray-300 rounded text-gray-900 font-medium"
                        >
                          <option value="comment">Comment</option>
                          <option value="note">Internal Note</option>
                          <option value="issue">Issue</option>
                          <option value="decision">Decision</option>
                          <option value="update">Update</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-gray-900 font-bold text-sm">Priority</Label>
                        <select
                          value={messagePriority}
                          onChange={(e) => setMessagePriority(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border-2 border-gray-300 rounded text-gray-900 font-medium"
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-900 font-bold text-sm">Message</Label>
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message here..."
                        className="mt-1 border-2 border-gray-300 text-gray-900 font-medium"
                        rows={3}
                      />
                    </div>
                    <Button
                      onClick={async () => {
                        if (!newMessage.trim()) {
                          toast.error('Message cannot be empty');
                          return;
                        }
                        setSendingMessage(true);
                        try {
                          await axios.post(`${API}/projects/${id}/messages`, {
                            message_type: messageType,
                            priority: messagePriority,
                            message: newMessage
                          });
                          toast.success('Message added successfully');
                          setNewMessage('');
                          loadMessages();
                        } catch (error) {
                          toast.error('Failed to send message');
                        } finally {
                          setSendingMessage(false);
                        }
                      }}
                      disabled={sendingMessage}
                      className="font-bold"
                    >
                      <Send size={16} className="mr-2" />
                      {sendingMessage ? 'Sending...' : 'Send Message'}
                    </Button>
                  </div>
                </div>

                {/* Messages List */}
                <div className="space-y-3">
                  <h3 className="font-bold text-gray-900 mb-3">Message History ({messages.length})</h3>
                  {messages.length > 0 ? (
                    messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`p-4 border-2 rounded-lg ${
                          msg.priority === 'urgent'
                            ? 'border-red-400 bg-red-50'
                            : msg.priority === 'high'
                            ? 'border-orange-400 bg-orange-50'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge
                              className={`text-xs font-bold ${
                                msg.message_type === 'issue'
                                  ? 'bg-red-200 text-red-900'
                                  : msg.message_type === 'decision'
                                  ? 'bg-purple-200 text-purple-900'
                                  : msg.message_type === 'note'
                                  ? 'bg-yellow-200 text-yellow-900'
                                  : 'bg-blue-200 text-blue-900'
                              }`}
                            >
                              {msg.message_type?.toUpperCase() || 'COMMENT'}
                            </Badge>
                            {msg.priority && msg.priority !== 'normal' && (
                              <Badge
                                variant="outline"
                                className={`text-xs font-bold ${
                                  msg.priority === 'urgent'
                                    ? 'border-red-500 text-red-700'
                                    : msg.priority === 'high'
                                    ? 'border-orange-500 text-orange-700'
                                    : 'border-gray-400 text-gray-700'
                                }`}
                              >
                                {msg.priority?.toUpperCase()}
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-gray-600 font-medium">
                            {msg.timestamp ? new Date(msg.timestamp).toLocaleString() : 'Just now'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900 font-medium whitespace-pre-wrap">
                          {msg.message}
                        </p>
                        {msg.user_name && (
                          <p className="text-xs text-gray-600 font-semibold mt-2">
                            — {msg.user_name}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-700 font-semibold">
                      No messages yet. Add the first message above.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Sub-Task Modal */}
      <Dialog open={showAddSubTaskModal} onOpenChange={setShowAddSubTaskModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {editingSubTask ? 'Edit Sub-Task' : 'Add New Sub-Task'}
            </DialogTitle>
            <DialogDescription className="text-gray-700 font-medium">
              {selectedMilestone && `For milestone: ${selectedMilestone.description}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-900 font-bold text-sm">Task Name *</Label>
              <Input
                value={subTaskForm.name}
                onChange={(e) => setSubTaskForm({ ...subTaskForm, name: e.target.value })}
                placeholder="e.g., Cut steel plates"
                className="mt-1 border-gray-300 text-gray-900 font-medium placeholder:text-gray-500"
              />
            </div>
            <div>
              <Label className="text-gray-900 font-bold text-sm">Description</Label>
              <Textarea
                value={subTaskForm.description}
                onChange={(e) => setSubTaskForm({ ...subTaskForm, description: e.target.value })}
                placeholder="Detailed description of the task..."
                className="mt-1 border-gray-300 text-gray-900 font-medium placeholder:text-gray-500"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-900 font-bold text-sm">Estimated Hours</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={subTaskForm.estimated_hours}
                  onChange={(e) => setSubTaskForm({ ...subTaskForm, estimated_hours: parseFloat(e.target.value) || 0 })}
                  className="mt-1 border-gray-300 text-gray-900 font-medium"
                />
              </div>
              <div>
                <Label className="text-gray-900 font-bold text-sm">Status</Label>
                <Select
                  value={subTaskForm.status}
                  onValueChange={(value) => setSubTaskForm({ ...subTaskForm, status: value })}
                >
                  <SelectTrigger className="mt-1 border-gray-300 text-gray-900 font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo" className="font-medium">To Do</SelectItem>
                    <SelectItem value="in_progress" className="font-medium">In Progress</SelectItem>
                    <SelectItem value="completed" className="font-medium">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-900 font-bold text-sm">Assigned To</Label>
                <Input
                  value={subTaskForm.assigned_to}
                  onChange={(e) => setSubTaskForm({ ...subTaskForm, assigned_to: e.target.value })}
                  placeholder="Staff member name"
                  className="mt-1 border-gray-300 text-gray-900 font-medium placeholder:text-gray-500"
                />
              </div>
              <div>
                <Label className="text-gray-900 font-bold text-sm">Due Date</Label>
                <Input
                  type="date"
                  value={subTaskForm.due_date}
                  onChange={(e) => setSubTaskForm({ ...subTaskForm, due_date: e.target.value })}
                  className="mt-1 border-gray-300 text-gray-900 font-medium"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowAddSubTaskModal(false)}
              className="border-gray-400 text-gray-900 font-bold hover:bg-gray-100"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSubTask}
              className="font-bold"
            >
              {editingSubTask ? 'Update' : 'Add'} Sub-Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Log Modal */}
      {showTimeLogModal && (
        <TimeLogModal
          projectId={id}
          projectName={project.project_name}
          onClose={() => {
            setShowTimeLogModal(false);
            loadProject();
          }}
        />
      )}

      {/* QC Sheet Selector Modal */}
      <QCSheetSelectorModal
        open={showQCModal}
        onClose={() => setShowQCModal(false)}
        projectId={id}
        projectName={project.project_name}
      />

      {/* Checklist Selector Modal */}
      <ChecklistSelectorModal
        open={showChecklistModal}
        onClose={() => setShowChecklistModal(false)}
        projectId={id}
        projectName={project.project_name}
      />
    </div>
  );
};

export default ProjectDetailEnhanced;
